import { useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, loggerLink } from "@trpc/client";
import { createTRPCReact } from "@trpc/react-query";
import superjson from "superjson";

import type { AppRouter } from "@samphire/api";

import { config } from "./config";

/**
 * A set of typesafe hooks for consuming your API.
 */
export const api = createTRPCReact<AppRouter>();

/**
 * Get the base URL for tRPC requests based on the current environment
 *
 * Environment-specific behavior:
 * - Development: Uses Vite proxy (localhost:3002/trpc -> localhost:3000/)
 * - Staging/Production: Direct API calls to the configured API URL
 */
function getBaseUrl() {
  console.log("🔧 Config debug:", {
    isDevelopment: config.isDevelopment,
    environment: config.environment,
    webUrl: config.webUrl,
    apiUrl: config.apiUrl,
  });

  if (config.isDevelopment) {
    // In development, use Vite proxy to avoid CORS issues
    const devUrl = `${config.webUrl}/trpc`;
    console.log("🌐 tRPC using development proxy URL:", devUrl);
    return devUrl;
  }

  // In staging/production, call API directly
  console.log("🌐 tRPC using direct API URL:", config.apiUrl);
  return config.apiUrl;
}

/**
 * A wrapper for your app that provides the TRPC context.
 * Use only in _app.tsx
 */
export function TRPCProvider({ children }: { children: React.ReactNode }) {
  console.log("TRPCProvider rendering...");

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: 1,
            refetchOnWindowFocus: false,
          },
        },
      }),
  );

  const [trpcClient] = useState(() => {
    const url = getBaseUrl();
    console.log("Creating tRPC client with URL:", url);

    const client = api.createClient({
      links: [
        loggerLink({
          enabled: (opts) =>
            import.meta.env.DEV ||
            (opts.direction === "down" && opts.result instanceof Error),
          colorMode: "ansi",
        }),
        httpBatchLink({
          transformer: superjson,
          url: url,
          fetch: async (url, options) => {
            console.log("🌐 tRPC client making request to:", url);

            // Check what cookies are available
            console.log("🍪 Current document cookies:", document.cookie);

            // Add credentials for cross-domain authentication
            const optionsWithCredentials = {
              ...options,
              credentials: "include" as RequestCredentials,
            };

            console.log(
              "📤 tRPC client request options:",
              optionsWithCredentials,
            );

            try {
              const response = await fetch(url, optionsWithCredentials);
              console.log("📥 tRPC response status:", response.status);
              console.log(
                "📥 tRPC response headers:",
                Object.fromEntries(response.headers.entries()),
              );

              // Clone response to read body for debugging
              const responseClone = response.clone();
              const responseText = await responseClone.text();
              console.log(
                "📥 tRPC response body preview:",
                responseText.substring(0, 200),
              );

              return response;
            } catch (error) {
              console.error("❌ tRPC fetch error:", error);
              throw error;
            }
          },
          async headers() {
            const headers: Record<string, string> = {};

            // Try to get session token for authentication
            try {
              // Import authClient dynamically to avoid circular dependencies
              const { authClient } = await import("../utils/auth-client");
              const session = await authClient.getSession();

              if (session?.data?.user) {
                console.log("🔐 Session found - relying on cookies for auth:", {
                  userId: session.data.user.id,
                  sessionId: session.data.session.id,
                });
                // Better Auth uses cookies - no need to add headers manually
              } else {
                console.log("🔐 No session found for tRPC headers");
              }
            } catch (error) {
              console.log("🔐 Error getting session for headers:", error);
            }

            console.log("Web app sending headers:", headers);
            return headers;
          },
        }),
      ],
    });

    console.log("tRPC client created:", client);
    return client;
  });

  return (
    <api.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </api.Provider>
  );
}
